<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

import {
  AccessDeniedError,
  NotFoundError,
  ROUND_STATE_LABEL,
  SIMPLE_STATUS_LABEL,
  dayLabel,
  demoApi,
  farmDateTime,
  farmTime,
  animalListStatus,
  loadAnimalList,
  loadEstimateItem,
  loadMapMarkers,
  markerIndicators,
  simpleStatus,
  type AnimalListItem,
  type CurrentReference,
  type DemoDirectoryItem,
  type MapContext,
  type MapMarker,
  type PublishedDay,
  type ReadingRound,
  type SimpleStatus,
} from '../../api/demo-client.js';
import { LOCAL_PREFIX, webAuth } from '../../auth/web-auth.js';
import AppModal from '../../components/AppModal.vue';
import {
  DayCache, currentKey, dayKey, sameContext, sameMarkers, type DayDetails, type DayEntry,
} from './day-cache.js';
import DayPicker from './DayPicker.vue';
import { MapView, READ_AGE_CLASS, mappable, readAgeBand, tileConfig, type ReadAgeBand } from './map-view.js';

/**
 * Área do cliente (M03-R3): cliente, fazenda e dia; quatro números; mapa
 * dominante; animais, histórico e detalhe em modais. Somente leitura (F19).
 *
 * Tempo: tudo vem pronto do servidor; a tela nunca converte data em instante
 * nem usa o relógio do navegador. Hoje (padrão) usa `current`: o agora do
 * servidor, renovado a cada 10 min com a página visível — idade da última
 * leitura, cores e números acompanham o tempo sem leitura nova. Dia publicado
 * usa o fim daquele dia e fica congelado, sem atualização. Toda resposta
 * carrega a geração em que foi pedida; resposta de geração antiga é descartada.
 *
 * Hoje sai da referência leve (`published-days?limit=0`): o servidor valida a
 * fazenda e devolve só `current`, sem consulta histórica; marcadores e contexto
 * vão em paralelo com EXATAMENTE esse instante. Dias publicados só são
 * buscados quando o calendário abre (ou o histórico de rodadas precisa deles),
 * em páginas, e ficam guardados na tela.
 *
 * Caminho rápido (M03-FP): mapa e números usam só marcadores + contexto.
 * Metadados vêm sob demanda: um alvo ao clicar, ou todos ao abrir "Animais".
 * Dia já visto reaparece do cache em memória e é revalidado: sem mudança
 * material (só o instante de Hoje avançou) não redesenha nem recarrega
 * metadados, a menos que alguma cor mude de faixa.
 */
const props = defineProps<{ openHistory?: boolean }>();

const POLL_MS = 10 * 60_000;
/** Dias por página do calendário: um mês cheio. */
const DAYS_PAGE = 31;

/** O que está na tela: Hoje ou um dia publicado de uma fazenda. */
interface DayView {
  readonly key: string;
  readonly farmId: string;
  /** Instante de referência resolvido pelo servidor para os marcadores mostrados. */
  readonly asOfMs: number;
  readonly markers: readonly MapMarker[];
  readonly etag: string | null;
  readonly context: MapContext;
  readonly details: DayDetails | null;
}

const tenants = ref<DemoDirectoryItem[]>([]);
const farms = ref<DemoDirectoryItem[]>([]);
const tenantId = ref('');
const farmId = ref('');
/** Dias publicados já carregados, mais recente primeiro e sem buraco até `olderBefore`. */
const days = ref<PublishedDay[]>([]);
const daysLoaded = ref(false);
/** `before` da próxima página; `null` = não há dia mais antigo (ou nada carregado). */
const olderBefore = ref<string | null>(null);
const daysLoading = ref(false);
const daysFailed = ref(false);
/** Hoje segundo o servidor; `null` só antes da primeira resposta da fazenda. */
const today = ref<CurrentReference | null>(null);
/** Acompanhando Hoje (padrão) ou parado num dia publicado (`selectedDate`). */
const followingToday = ref(true);
const selectedDate = ref<string | null>(null);
const view = shallowRef<DayView | null>(null);
const detailsFailed = ref(false);
const selectedKey = ref<string | null>(null);
const modal = ref<'animals' | 'history' | 'detail' | null>(props.openHistory === true ? 'history' : null);
const loading = ref(false);
const directoryLoaded = ref(false);
const failed = ref(false);
const disabled = ref(false);
const baseLayerAvailable = ref(true);
const search = ref('');
const statusFilter = ref<SimpleStatus | ''>('');
const rounds = ref<ReadingRound[]>([]);
const roundsCursor = ref<string | null>(null);
const roundsLoading = ref(false);
const roundsFailed = ref(false);

const mapElement = ref<HTMLElement | null>(null);
let mapView: MapView | null = null;
let poll: ReturnType<typeof setInterval> | null = null;
let generation = 0;
let roundsGeneration = 0;
let daysGeneration = 0;
let daysTask: Promise<void> = Promise.resolve();
const cache = new DayCache(32);

const STATUSES: SimpleStatus[] = ['located', 'approximate', 'no_recent_read', 'unlocated'];
const LEGEND: { band: ReadAgeBand; label: string }[] = [
  { band: 'recent', label: 'Menos de 1 dia' },
  { band: 'one_day', label: 'Mais de 1 dia' },
  { band: 'two_days', label: '2 dias ou mais' },
];

const farmName = computed(() => farms.value.find((farm) => farm.id === farmId.value)?.name ?? '');
const shownDate = computed(() => (followingToday.value ? (today.value?.date ?? null) : selectedDate.value));
const availableDates = computed(() => new Set(days.value.map((day) => day.date)));
const kpis = computed(() => {
  const shown = view.value;
  return shown === null
    ? { total: 0, located: 0, unlocated: 0, noRecentRead: 0 }
    : markerIndicators(shown.markers, shown.asOfMs);
});
const detailsLoading = computed(() => view.value !== null && view.value.details?.complete !== true);
const rows = computed(() => {
  const shown = view.value;
  const reference = shown?.asOfMs ?? 0;
  const onMap = new Set(shown?.markers.filter((marker) => mappable(marker, shown!.farmId)).map((marker) => marker.targetKey));
  return (shown?.details?.complete === true ? shown.details.items : []).map((item) => ({
    item,
    status: animalListStatus(item, reference),
    onMap: onMap.has(item.targetKey),
  }));
});
const filteredRows = computed(() => {
  const term = search.value.trim().toUpperCase();
  return rows.value.filter(
    (row) => (term === '' || row.item.epc.includes(term)) && (statusFilter.value === '' || row.status === statusFilter.value),
  );
});
const selectedRow = computed(() => {
  const item = selectedKey.value === null ? undefined : view.value?.details?.byKey.get(selectedKey.value);
  return item === undefined || view.value === null ? null : { item, status: simpleStatus(item, view.value.asOfMs), onMap: true };
});
const selectedMarker = computed(() => view.value?.markers.find((marker) => marker.targetKey === selectedKey.value) ?? null);
/** Nenhum alvo com leitura no instante exibido: não há o que localizar. */
const noReadings = computed(() => view.value !== null && view.value.markers.every((marker) => marker.lastReadAtMs === null));

function fail(caught: unknown): void {
  if (caught instanceof AccessDeniedError) disabled.value = true;
  else failed.value = true;
}

/**
 * Último cliente escolhido: só PREFERÊNCIA local. Quem decide o escopo é a
 * API; preferência fora da lista autorizada ou negada depois é descartada.
 */
const TENANT_PREFERENCE = `${LOCAL_PREFIX}cliente.tenantId`;
function preference(): string | null {
  try {
    return localStorage.getItem(TENANT_PREFERENCE);
  } catch {
    return null;
  }
}
function remember(value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(TENANT_PREFERENCE);
    else localStorage.setItem(TENANT_PREFERENCE, value);
  } catch {
    // Sem storage, sem preferência: a tela continua funcionando.
  }
}

async function loadDirectory(): Promise<void> {
  failed.value = false;
  try {
    tenants.value = (await demoApi.tenants()).items;
    directoryLoaded.value = true;
    const saved = preference();
    if (saved !== null && !tenants.value.some((tenant) => tenant.id === saved)) remember(null);
    if (tenantId.value === '' && tenants.value.length > 0) {
      tenantId.value = tenants.value.find((tenant) => tenant.id === saved)?.id ?? tenants.value[0]!.id;
    }
  } catch (caught) {
    fail(caught);
  }
}

async function loadFarms(): Promise<void> {
  const requested = tenantId.value;
  try {
    const loaded = (await demoApi.farms(requested)).items;
    if (requested !== tenantId.value) return;
    farms.value = loaded;
    remember(requested);
    if (loaded.length > 0) farmId.value = loaded[0]!.id;
  } catch (caught) {
    if (caught instanceof NotFoundError && requested === tenantId.value) {
      // A API negou o cliente (acesso revogado): esquece a preferência e recarrega a lista.
      remember(null);
      tenantId.value = '';
      await loadDirectory();
      return;
    }
    fail(caught);
  }
}

/** Troca de fazenda: descarta tudo da anterior (também do mapa), busca só Hoje e abre Hoje. */
async function loadFarm(): Promise<void> {
  const current = ++generation;
  stopPolling();
  view.value = null;
  mapView?.clear();
  selectedKey.value = null;
  daysGeneration += 1;
  // Uma consulta lenta da fazenda anterior não pode bloquear o calendário da nova.
  daysTask = Promise.resolve();
  days.value = [];
  daysLoaded.value = false;
  olderBefore.value = null;
  daysFailed.value = false;
  daysLoading.value = false;
  today.value = null;
  followingToday.value = true;
  selectedDate.value = null;
  rounds.value = [];
  roundsCursor.value = null;
  if (farmId.value === '') return;
  if (modal.value === 'history') void loadRounds(true);
  loading.value = true;
  failed.value = false;
  try {
    const loaded = await demoApi.publishedDays(tenantId.value, farmId.value, { limit: 0 });
    if (current !== generation) return;
    today.value = loaded.current;
    await loadSnapshot();
  } catch (caught) {
    if (current === generation) {
      denied(caught, tenantId.value, farmId.value);
      fail(caught);
      loading.value = false;
    }
  }
}

/** Chave do cache e instante consultado da seleção: Hoje usa a entrada estável e o agora do servidor. */
function target(tenant: string, farm: string): { key: string; asOf: number } | null {
  if (followingToday.value) {
    return today.value === null ? null : { key: currentKey(tenant, farm), asOf: today.value.asOfMs };
  }
  const day = days.value.find((entry) => entry.date === selectedDate.value);
  return day === undefined ? null : { key: dayKey(tenant, farm, day.asOfMs), asOf: day.asOfMs };
}

/** A referência da tela é a dos marcadores guardados, nunca uma mais nova sem eles. */
function fromEntry(key: string, farm: string, entry: DayEntry): DayView {
  const markers = entry.markers!;
  return {
    key, farmId: farm, asOfMs: markers.resolvedAsOfMs, markers: markers.markers, etag: markers.etag,
    context: entry.context!, details: entry.details ?? null,
  };
}

async function show(next: DayView): Promise<void> {
  view.value = next;
  await nextTick();
  renderMap();
}

/** 401/403 esquece o cache inteiro; 404 (fora do escopo) esquece a fazenda e tira o dado da tela. */
function denied(caught: unknown, tenant: string, farm: string): boolean {
  if (caught instanceof AccessDeniedError) {
    cache.clear();
    return true;
  }
  if (caught instanceof NotFoundError) {
    cache.forget(tenant, farm);
    view.value = null;
    mapView?.clear();
    return true;
  }
  return false;
}

/** Algum marcador do mapa troca de faixa de idade entre as duas referências. */
function bandsMoved(markers: readonly MapMarker[], farm: string, from: number, to: number): boolean {
  return markers.some((marker) =>
    mappable(marker, farm) && readAgeBand(marker.lastReadAtMs, from) !== readAgeBand(marker.lastReadAtMs, to));
}

/**
 * Seleção atual. Com cache, a tela aparece na hora e a rede só revalida;
 * falha de revalidação mantém o que está na tela. Sem cache, marcadores e
 * contexto em paralelo e o mapa anterior fica até eles chegarem.
 *
 * `refreshToday` (atualização e volta para Hoje): antes de consultar, pede de
 * novo `current` ao servidor e usa EXATAMENTE esse instante. Hoje sem mudança
 * material só troca a referência (números e lista); o mapa só é redesenhado
 * se uma cor mudar de faixa, e metadados não são recarregados.
 */
async function loadSnapshot(refreshToday = false): Promise<void> {
  const current = ++generation;
  const tenant = tenantId.value;
  const farm = farmId.value;
  loading.value = true;
  failed.value = false;
  let hasCache = false;
  try {
    let wanted = target(tenant, farm);
    if (wanted === null) return;
    const cached = cache.get(wanted.key);
    hasCache = cached?.markers !== undefined && cached.context !== undefined;
    if (hasCache && view.value?.key !== wanted.key) await show(fromEntry(wanted.key, farm, cached!));
    if (refreshToday) {
      const loaded = await demoApi.publishedDays(tenant, farm, { limit: 0 });
      if (current !== generation) return;
      today.value = loaded.current;
      wanted = target(tenant, farm)!;
    }
    const { key, asOf } = wanted;
    // Histórico tem geometria fixa; Hoje usa a vigente agora (pode ganhar pasto novo).
    const refreshContext = cached?.context === undefined || followingToday.value;
    // Promessa compartilhada por instante: a de Hoje anterior não responde pela nova.
    const [markers, context] = await Promise.all([
      cache.shared('markers', `${key}@${asOf}`, () => loadMapMarkers(tenant, farm, asOf, cached?.markers?.etag ?? null)),
      refreshContext
        ? cache.shared('context', `${key}@${asOf}`, () => demoApi.mapContext(tenant, farm, asOf))
        : Promise.resolve(cached!.context!),
    ]);
    const previous = cache.get(key);
    // Resposta de Hoje mais velha que a guardada não faz o cache regredir.
    if (markers !== null && previous?.markers !== undefined && previous.markers.resolvedAsOfMs > markers.resolvedAsOfMs) return;
    // Sem mudança material, o MESMO array: identidade = conteúdo, para tela e metadados.
    const next = markers === null || !sameMarkers(previous?.markers, markers)
      ? markers
      : { ...markers, markers: previous!.markers!.markers };
    const contextChanged = !sameContext(previous?.context, context);
    const material = (next !== null && next.markers !== previous?.markers?.markers) || contextChanged;
    const details = previous?.details;
    const entry = cache.update(key, {
      ...(next === null ? {} : { markers: next }),
      context,
      // Mudança material: lista continua visível até a nova; detalhes avulsos
      // de outros animais saem (voltam sob demanda), o aberto é renovado.
      ...(material && details !== undefined
        ? { details: { ...details, byKey: new Map([...details.byKey].filter(([targetKey]) => targetKey === selectedKey.value)) } }
        : {}),
    });
    if (current !== generation) return;
    const shown = view.value;
    const reference = entry.markers!.resolvedAsOfMs;
    if (shown === null || shown.key !== key || material || bandsMoved(shown.markers, farm, shown.asOfMs, reference)) {
      await show(fromEntry(key, farm, entry));
    } else if (shown.asOfMs !== reference) {
      view.value = { ...shown, asOfMs: reference, etag: entry.markers!.etag };
    }
    if (material && entry.details !== undefined) refreshDetails(key, farm, reference, entry.details);
  } catch (caught) {
    if (current !== generation) return;
    // Revalidação que falha com cache na tela: continua mostrando o cache.
    if (denied(caught, tenant, farm) || !hasCache) fail(caught);
  } finally {
    if (current === generation) loading.value = false;
  }
  if (current === generation) schedulePolling();
}

/** Metadados já carregados ficaram velhos: renova em segundo plano, sem esvaziar a tela. */
function refreshDetails(key: string, farm: string, asOf: number, details: DayDetails): void {
  if (details.complete) void loadDetails(key, tenantId.value, farm, asOf);
  if (selectedKey.value !== null && modal.value === 'detail') void loadOneDetail(selectedKey.value, true);
}

/**
 * Lista completa sob demanda, ao abrir "Animais", no MESMO instante dos
 * marcadores. Indexa por alvo sem redesenhar mapa; resposta de outra seleção
 * fica só no cache dela.
 */
async function loadDetails(key: string, tenant: string, farm: string, asOf: number): Promise<void> {
  const requestedFor = cache.get(key)?.markers?.markers;
  detailsFailed.value = false;
  try {
    const items = await cache.shared('details', `${key}@${asOf}`, () => loadAnimalList(tenant, farm, asOf));
    const currentMarkers = cache.get(key)?.markers;
    if (currentMarkers === undefined) return;
    // Promessa compartilhada: quem chegar depois encontra o índice já aplicado.
    const existing = cache.get(key)?.details;
    if (existing?.items === items && view.value?.details?.items === items) return;
    const details: DayDetails = { items, byKey: existing?.byKey ?? new Map(), complete: true };
    cache.update(key, { details });
    const shown = view.value;
    if (shown !== null && shown.key === key) {
      view.value = { ...shown, details };
      mapView?.setDetails(markerDetails(details));
    }
    // Se os alvos mudaram durante uma consulta lenta, mostra esta resposta
    // em vez de skeleton e busca a versão atual em seguida.
    if (requestedFor !== currentMarkers.markers) void loadDetails(key, tenant, farm, currentMarkers.resolvedAsOfMs);
  } catch (caught) {
    if (view.value?.key !== key) return;
    if (denied(caught, tenant, farm)) fail(caught);
    else detailsFailed.value = true;
  }
}

async function loadOneDetail(key: string, force = false): Promise<void> {
  const shown = view.value;
  if (shown === null || (!force && shown.details?.byKey.has(key) === true)) return;
  const cacheKey = shown.key;
  const asOf = shown.asOfMs;
  detailsFailed.value = false;
  try {
    const item = await cache.shared('detail', `${cacheKey}@${asOf}/${key}`, () =>
      loadEstimateItem(tenantId.value, shown.farmId, asOf, key));
    const currentAsOf = cache.get(cacheKey)?.markers?.resolvedAsOfMs;
    if (currentAsOf !== asOf) {
      if (view.value?.key === cacheKey && modal.value === 'detail' && selectedKey.value === key) {
        void loadOneDetail(key, true);
      }
      return;
    }
    const current = cache.get(cacheKey)?.details;
    const byKey = new Map(current?.byKey ?? []);
    byKey.set(key, item);
    const details: DayDetails = { items: current?.items ?? [], byKey, complete: current?.complete === true };
    cache.update(cacheKey, { details });
    if (view.value?.key === cacheKey) {
      view.value = { ...view.value, details };
      mapView?.setDetails(markerDetails(details));
    }
  } catch {
    if (view.value?.key === cacheKey) detailsFailed.value = true;
  }
}

function retryDetails(): void {
  const shown = view.value;
  if (shown !== null) void loadDetails(shown.key, tenantId.value, shown.farmId, shown.asOfMs);
}

function renderMap(): void {
  const shown = view.value;
  if (shown === null || mapElement.value === null) return;
  if (mapView === null) {
    mapView = new MapView(
      mapElement.value,
      tileConfig(import.meta.env),
      (key) => openDetail(key),
      (available) => (baseLayerAvailable.value = available),
    );
  }
  // Cor pela idade no instante consultado (agora do servidor em Hoje).
  mapView.render(
    shown.farmId,
    shown.context,
    shown.markers,
    selectedKey.value,
    shown.asOfMs,
    shown.details === null ? null : markerDetails(shown.details),
  );
}

function markerDetails(details: DayDetails): ReadonlyMap<string, Pick<AnimalListItem, 'epc' | 'pastureName' | 'expectedPastureName'>> {
  const values = new Map<string, Pick<AnimalListItem, 'epc' | 'pastureName' | 'expectedPastureName'>>(
    details.items.map((item) => [item.targetKey, item]),
  );
  for (const [key, item] of details.byKey) values.set(key, item);
  return values;
}

/**
 * Dias publicados sob demanda, uma chamada por vez (a ordem importa: cada
 * página parte do `nextBefore` da anterior).
 *
 * - `fresh` (calendário aberto): renova a primeira página — um dia publicado
 *   enquanto a tela estava aberta aparece — e mantém as páginas mais antigas
 *   já carregadas abaixo dela;
 * - `through`: continua paginando até cobrir todos os dias a partir dessa
 *   data. Paginação causal: cada pedido depende do cursor do anterior, e são
 *   só as páginas que o usuário navegou (cada uma cobre ao menos um mês).
 */
function loadDays(fresh: boolean, through: string | null): Promise<void> {
  const tenant = tenantId.value;
  const farm = farmId.value;
  const owner = daysGeneration;
  const run = async () => {
    if (owner !== daysGeneration || farm === '') return;
    daysLoading.value = true;
    daysFailed.value = false;
    try {
      if (fresh || !daysLoaded.value) {
        const page = await demoApi.publishedDays(tenant, farm, { limit: DAYS_PAGE });
        if (owner !== daysGeneration) return;
        days.value = mergeHead(page.days, page.nextBefore);
        daysLoaded.value = true;
      }
      while (through !== null && olderBefore.value !== null && olderBefore.value > through) {
        const page = await demoApi.publishedDays(tenant, farm, { limit: DAYS_PAGE, before: olderBefore.value });
        if (owner !== daysGeneration) return;
        days.value = [...days.value, ...page.days];
        olderBefore.value = page.nextBefore;
      }
    } catch (caught) {
      if (owner !== daysGeneration) return;
      if (denied(caught, tenant, farm)) fail(caught);
      else daysFailed.value = true;
    } finally {
      if (owner === daysGeneration) daysLoading.value = false;
    }
  };
  daysTask = daysTask.then(run);
  return daysTask;
}

/**
 * Nova primeira página sobre o que já estava carregado. Cada lista está
 * completa a partir do seu cursor (`null` = até o início); abaixo do cursor
 * novo, valem os dias antigos, e a cobertura final é a mais funda das duas.
 */
function mergeHead(head: PublishedDay[], headBefore: string | null): PublishedDay[] {
  const previous = olderBefore.value;
  if (headBefore === null || !daysLoaded.value) {
    olderBefore.value = headBefore;
    return head;
  }
  olderBefore.value = previous === null || previous < headBefore ? previous : headBefore;
  return [...head, ...days.value.filter((day) => day.date < headBefore)];
}

/** Hoje (mesmo com leitura publicada hoje: uma opção só) ou um dia publicado; o resto é recusado. */
function chooseDay(date: string): void {
  if (today.value !== null && date === today.value.date) {
    followingToday.value = true;
    selectedDate.value = null;
    void loadSnapshot(true);
    return;
  }
  if (availableDates.value.has(date)) {
    selectDay(date);
    return;
  }
  // Dia ainda não carregado (ex.: rodada antiga do histórico): busca até ele e decide.
  if (!daysLoaded.value || (olderBefore.value !== null && olderBefore.value > date)) {
    const owner = daysGeneration;
    void loadDays(false, date).then(() => {
      if (owner === daysGeneration && availableDates.value.has(date)) selectDay(date);
    });
  }
}

function selectDay(date: string): void {
  followingToday.value = false;
  selectedDate.value = date;
  stopPolling();
  void loadSnapshot();
}

function stopPolling(): void {
  if (poll !== null) clearInterval(poll);
  poll = null;
}

/** Só Hoje se atualiza sozinho; dia publicado é histórico congelado. */
function schedulePolling(): void {
  stopPolling();
  if (disabled.value || farmId.value === '' || !followingToday.value) return;
  poll = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') void loadSnapshot(true);
  }, POLL_MS);
}

function retry(): void {
  if (!directoryLoaded.value) void loadDirectory();
  else if (farmId.value !== '' && today.value === null) void loadFarm();
  else void loadSnapshot(followingToday.value);
}

function openDetail(key: string): void {
  selectedKey.value = key;
  mapView?.select(key);
  modal.value = 'detail';
  void loadOneDetail(key);
}

function openModal(which: 'animals' | 'history'): void {
  modal.value = which;
  if (which === 'history') void loadRounds(true);
  else if (view.value?.details?.complete !== true) retryDetails();
}

async function loadRounds(reset: boolean): Promise<void> {
  if (tenantId.value === '' || farmId.value === '') return;
  const current = ++roundsGeneration;
  roundsLoading.value = true;
  roundsFailed.value = false;
  try {
    const page = await demoApi.readingRounds(tenantId.value, farmId.value, reset ? undefined : (roundsCursor.value ?? undefined));
    if (current !== roundsGeneration) return;
    rounds.value = reset ? page.items : [...rounds.value, ...page.items];
    roundsCursor.value = page.nextCursor;
    // Rodada concluída só é selecionável se o dia dela está publicado: carrega os
    // dias até a mais antiga (abrir o histórico renova a primeira página).
    const oldest = rounds.value.filter((round) => round.state === 'completed').at(-1)?.date;
    if (oldest !== undefined) void loadDays(reset, oldest);
  } catch (caught) {
    if (current !== roundsGeneration) return;
    if (caught instanceof AccessDeniedError) disabled.value = true;
    else roundsFailed.value = true;
  } finally {
    if (current === roundsGeneration) roundsLoading.value = false;
  }
}

function roundSelectable(round: ReadingRound): boolean {
  return round.state === 'completed' && availableDates.value.has(round.date);
}

function pickRound(round: ReadingRound): void {
  if (!roundSelectable(round)) return;
  modal.value = null;
  chooseDay(round.date);
}

watch(tenantId, () => {
  farmId.value = '';
  farms.value = [];
  if (tenantId.value !== '') void loadFarms();
});
watch(farmId, () => void loadFarm());

onMounted(() => void loadDirectory());
onBeforeUnmount(() => {
  stopPolling();
  cache.clear();
  mapView?.destroy();
  mapView = null;
});

function logout(): void {
  cache.clear();
  void webAuth().logout();
}

defineExpose({ chooseDay, openDetail });
</script>

<template>
  <div class="client-page">
    <header class="topbar">
      <div class="brand">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="22"
          height="22"
        ><path
          d="M12 21c5-3 8-7 8-12a8 8 0 0 0-16 0c0 5 3 9 8 12z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        /><circle
          cx="12"
          cy="9"
          r="2.6"
          fill="currentColor"
        /></svg>
        <span>Sigfarm <strong>RFID</strong></span>
      </div>
      <template v-if="!disabled">
        <!-- Um cliente: selecionado sozinho; vários: seletor. -->
        <label
          v-if="tenants.length > 1"
          class="field"
        >
          <span class="field-label">Cliente</span>
          <select v-model="tenantId">
            <option
              v-for="tenant in tenants"
              :key="tenant.id"
              :value="tenant.id"
            >{{ tenant.name }}</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">Fazenda</span>
          <select v-model="farmId">
            <option
              v-for="farm in farms"
              :key="farm.id"
              :value="farm.id"
            >{{ farm.name }}</option>
          </select>
        </label>
        <DayPicker
          :days="days"
          :today="today?.date ?? null"
          :model-value="shownDate"
          :has-older="olderBefore !== null"
          :loading="daysLoading"
          :failed="daysFailed"
          @update:model-value="chooseDay"
          @open="(monthStart) => loadDays(true, monthStart)"
          @month="(monthStart) => loadDays(false, monthStart)"
        />
        <div class="actions">
          <button
            type="button"
            class="button"
            :disabled="farmId === ''"
            @click="openModal('animals')"
          >
            Animais
          </button>
          <button
            type="button"
            class="button"
            :disabled="farmId === ''"
            @click="openModal('history')"
          >
            Histórico de leituras
          </button>
          <button
            type="button"
            class="button"
            @click="logout"
          >
            Sair
          </button>
        </div>
      </template>
    </header>

    <p
      v-if="disabled"
      class="notice"
      role="status"
    >
      Acesso indisponível no momento.
    </p>

    <template v-else>
      <div
        v-if="failed"
        class="notice notice--error"
        role="alert"
      >
        <span>Não foi possível carregar os dados.</span>
        <button
          type="button"
          class="button"
          @click="retry"
        >
          Tentar novamente
        </button>
      </div>
      <p
        v-else-if="directoryLoaded && tenants.length === 0"
        class="notice"
        role="status"
      >
        Nenhum cliente disponível.
      </p>

      <section
        class="kpis"
        aria-label="Indicadores"
      >
        <div class="kpi">
          <span class="kpi__label">Total de animais</span>
          <span class="kpi__value">{{ kpis.total }}</span>
        </div>
        <div class="kpi">
          <span class="kpi__label">Localizados</span>
          <span class="kpi__value">{{ kpis.located }}</span>
        </div>
        <div class="kpi">
          <span class="kpi__label">Sem localização</span>
          <span class="kpi__value">{{ kpis.unlocated }}</span>
        </div>
        <div class="kpi">
          <span class="kpi__label">Sem leitura recente</span>
          <span class="kpi__value">{{ kpis.noRecentRead }}</span>
        </div>
      </section>

      <section class="map-card">
        <div
          ref="mapElement"
          class="map"
          role="region"
          :aria-label="farmName === '' ? 'Mapa' : `Mapa da ${farmName}`"
        />
        <p
          v-if="loading"
          class="map-badge"
          role="status"
        >
          Atualizando…
        </p>
        <p
          v-if="noReadings && !loading"
          class="map-empty"
          role="status"
        >
          Nenhuma leitura disponível.
        </p>
        <ul
          class="map-legend"
          aria-label="Última leitura"
        >
          <li
            v-for="entry in LEGEND"
            :key="entry.band"
          >
            <span
              :class="['map-legend__dot', READ_AGE_CLASS[entry.band]]"
              aria-hidden="true"
            />{{ entry.label }}
          </li>
        </ul>
        <p
          v-if="!baseLayerAvailable"
          class="map-warning"
          role="status"
        >
          Imagem de satélite indisponível.
        </p>
      </section>
    </template>

    <AppModal
      v-if="modal === 'detail' && selectedMarker !== null"
      title="Animal"
      @close="modal = null"
    >
      <div
        v-if="detailsFailed && selectedRow === null"
        class="notice notice--error"
        role="alert"
      >
        <span>Não foi possível carregar os detalhes.</span>
        <button
          type="button"
          class="button"
          @click="retryDetails"
        >
          Tentar novamente
        </button>
      </div>
      <dl
        v-if="selectedRow !== null"
        class="detail"
      >
        <dt>Etiqueta</dt><dd class="mono">
          {{ selectedRow.item.epc }}
        </dd>
        <dt>Estado</dt><dd>{{ SIMPLE_STATUS_LABEL[selectedRow.status] }}</dd>
        <dt>Pasto calculado</dt><dd>{{ selectedRow.item.pastureName ?? '—' }}</dd>
        <dt>Pasto esperado</dt><dd>{{ selectedRow.item.expectedPastureName ?? '—' }}</dd>
        <dt>Última leitura</dt><dd>{{ farmDateTime(selectedRow.item.lastReadAtMs) }}</dd>
      </dl>
      <dl
        v-else
        class="detail"
        :aria-busy="!detailsFailed"
      >
        <!-- Antes dos metadados: forma do detalhe com o que o marcador já sabe. -->
        <template
          v-for="label in ['Etiqueta', 'Estado', 'Pasto calculado', 'Pasto esperado']"
          :key="label"
        >
          <dt>{{ label }}</dt><dd>
            <span
              v-if="!detailsFailed"
              class="skeleton-line"
              aria-hidden="true"
            /><template v-else>
              —
            </template>
          </dd>
        </template>
        <dt>Última leitura</dt><dd>{{ farmDateTime(selectedMarker.lastReadAtMs) }}</dd>
      </dl>
    </AppModal>

    <AppModal
      v-if="modal === 'animals'"
      title="Animais"
      wide
      @close="modal = null"
    >
      <div class="modal-filters">
        <label class="field field--grow">
          <span class="field-label">Pesquisar etiqueta</span>
          <input
            v-model="search"
            type="search"
            data-autofocus
            autocomplete="off"
          >
        </label>
        <label class="field">
          <span class="field-label">Estado</span>
          <select v-model="statusFilter">
            <option value="">Todos</option>
            <option
              v-for="status in STATUSES"
              :key="status"
              :value="status"
            >{{ SIMPLE_STATUS_LABEL[status] }}</option>
          </select>
        </label>
      </div>
      <div
        v-if="detailsFailed && rows.length === 0"
        class="notice notice--error"
        role="alert"
      >
        <span>Não foi possível carregar os animais.</span>
        <button
          type="button"
          class="button"
          @click="retryDetails"
        >
          Tentar novamente
        </button>
      </div>
      <div
        v-else-if="detailsLoading"
        class="table-skeleton"
        aria-busy="true"
      >
        <span
          v-for="index in 6"
          :key="index"
          class="skeleton-line"
          aria-hidden="true"
        />
      </div>
      <p
        v-else-if="filteredRows.length === 0"
        class="empty"
      >
        Nenhum animal encontrado.
      </p>
      <div
        v-else
        class="table-wrap"
      >
        <table>
          <caption class="sr-only">
            {{ filteredRows.length }} animais
          </caption>
          <thead>
            <tr>
              <th scope="col">
                Etiqueta
              </th>
              <th scope="col">
                Estado
              </th>
              <th scope="col">
                Pasto calculado
              </th>
              <th scope="col">
                Pasto esperado
              </th>
              <th scope="col">
                Última leitura
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredRows"
              :key="row.item.targetKey"
            >
              <td class="mono">
                <button
                  v-if="row.onMap"
                  type="button"
                  class="link"
                  @click="openDetail(row.item.targetKey)"
                >
                  {{ row.item.epc }}
                </button>
                <span v-else>{{ row.item.epc }}</span>
              </td>
              <td>
                <span :class="`status status--${row.status}`">{{ SIMPLE_STATUS_LABEL[row.status] }}</span>
              </td>
              <td>{{ row.item.pastureName ?? '—' }}</td>
              <td>{{ row.item.expectedPastureName ?? '—' }}</td>
              <td>{{ farmDateTime(row.item.lastReadAtMs) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </AppModal>

    <AppModal
      v-if="modal === 'history'"
      title="Histórico de leituras"
      wide
      @close="modal = null"
    >
      <div
        v-if="roundsFailed"
        class="notice notice--error"
        role="alert"
      >
        <span>Não foi possível carregar o histórico.</span>
        <button
          type="button"
          class="button"
          @click="loadRounds(true)"
        >
          Tentar novamente
        </button>
      </div>
      <p
        v-else-if="farmId !== '' && !roundsLoading && rounds.length === 0"
        class="empty"
      >
        Nenhuma leitura registrada.
      </p>
      <div
        v-if="rounds.length > 0"
        class="table-wrap"
      >
        <table>
          <thead>
            <tr>
              <th scope="col">
                Dia
              </th>
              <th scope="col">
                Início
              </th>
              <th scope="col">
                Término
              </th>
              <th scope="col">
                Etiquetas lidas
              </th>
              <th scope="col">
                Estado
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="round in rounds"
              :key="round.sessionId"
            >
              <td>
                <button
                  v-if="roundSelectable(round)"
                  type="button"
                  class="link"
                  @click="pickRound(round)"
                >
                  {{ dayLabel(round.date) }}
                </button>
                <span v-else>{{ dayLabel(round.date) }}</span>
              </td>
              <td>{{ farmTime(round.startedAtMs) }}</td>
              <td>{{ farmTime(round.endedAtMs) }}</td>
              <td>{{ round.tagCount }}</td>
              <td>{{ ROUND_STATE_LABEL[round.state] }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p
        v-if="roundsLoading"
        class="empty"
        role="status"
      >
        Carregando…
      </p>
      <button
        v-if="roundsCursor !== null && !roundsLoading"
        type="button"
        class="button more"
        @click="loadRounds(false)"
      >
        Mostrar mais
      </button>
    </AppModal>
  </div>
</template>

<style scoped>
.client-page {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 100dvh;
  padding: 0.75rem 1rem 1rem;
  box-sizing: border-box;
}
.topbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.brand {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-right: auto;
  align-self: center;
  color: var(--primary-strong);
  font-size: 1.05rem;
  white-space: nowrap;
}
.brand span { color: var(--foreground); }
.actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}
.kpi {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.7rem 1rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.kpi__label { font-size: 0.8rem; color: var(--muted-foreground); }
.kpi__value { font-size: 1.6rem; font-weight: 700; line-height: 1.1; font-variant-numeric: tabular-nums; }
.map-card {
  position: relative;
  flex: 1 1 auto;
  min-height: 24rem;
  display: flex;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.map { flex: 1 1 auto; min-height: 24rem; }
.map-badge, .map-empty, .map-warning {
  position: absolute;
  z-index: 1000;
  margin: 0;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #fff;
  box-shadow: var(--shadow);
  font-size: 0.85rem;
}
.map-badge { top: 0.75rem; right: 0.75rem; }
.map-warning { bottom: 1.75rem; left: 0.75rem; background: hsl(48 100% 96%); border: 1px solid hsl(38 92% 50%); }
.map-legend {
  position: absolute;
  z-index: 1000;
  right: 0.75rem;
  bottom: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0;
  padding: 0.4rem 0.6rem;
  list-style: none;
  border-radius: var(--radius);
  background: #fff;
  box-shadow: var(--shadow);
  font-size: 0.75rem;
}
.map-legend li { display: flex; align-items: center; gap: 0.4rem; }
.map-legend__dot {
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border: 2px solid hsl(222 47% 11%);
  border-radius: 50%;
}
.map-empty {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
}
.notice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  padding: 0.6rem 1rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.notice--error { border-color: hsl(0 74% 50% / 0.5); background: hsl(0 86% 97%); }
.modal-filters { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 0.75rem; }
.field--grow { flex: 1 1 14rem; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
th, td { padding: 0.5rem 0.6rem; text-align: left; border-bottom: 1px solid var(--border); white-space: nowrap; }
th { font-size: 0.78rem; font-weight: 600; color: var(--muted-foreground); }
.detail { display: grid; grid-template-columns: max-content 1fr; gap: 0.6rem 1.25rem; margin: 0; }
.detail dt { color: var(--muted-foreground); }
.detail dd { margin: 0; font-weight: 600; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.85rem; }
.status { display: inline-block; padding: 0.1rem 0.55rem; border-radius: 999px; font-size: 0.78rem; background: var(--secondary); }
.status--located { background: hsl(142 72% 94%); color: hsl(142 72% 22%); }
.status--approximate { background: hsl(38 92% 92%); color: hsl(28 80% 28%); }
.status--no_recent_read { background: hsl(0 86% 95%); color: hsl(0 70% 35%); }
.empty { margin: 0.5rem 0; color: var(--muted-foreground); }
/* Espera pelos metadados: forma estática, sem animação (nada a desligar em movimento reduzido). */
.skeleton-line { display: inline-block; width: 8rem; max-width: 100%; height: 0.8rem; border-radius: 0.3rem; background: hsl(150 8% 88%); vertical-align: middle; }
.table-skeleton { display: grid; gap: 0.6rem; padding: 0.5rem 0; }
.table-skeleton .skeleton-line { width: 100%; height: 1.1rem; }
.more { margin-top: 0.75rem; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (max-width: 900px) {
  .kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .client-page { padding: 0.5rem; gap: 0.5rem; }
  .topbar { padding: 0.6rem 0.75rem; }
  .brand { width: 100%; }
  .field, .actions, .day-picker { flex: 1 1 100%; }
  .day-picker :deep(.day-picker__trigger) { width: 100%; }
  .actions .button { flex: 1 1 0; }
  .kpi { padding: 0.55rem 0.75rem; }
  .kpi__value { font-size: 1.3rem; }
  .map-card, .map { min-height: 60dvh; }
}
</style>
